import React, { useRef, useState } from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { StepProps } from '@/src/types';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createInvitation, publishInvitation, getInvitationStatus, ApiError } from '@/src/infrastructure/lib/api';
import { usePublishEntitlement } from '@/src/modules/entitlements/usePublishEntitlement';
import { InvitationPublishPreviewCard } from './InvitationPublishPreviewCard';
import { DraftPreviewModal } from './DraftPreviewModal';
import { TopLayerDialog } from './TopLayerDialog';
import { InsufficientWalletModal } from '@/src/modules/components/InsufficientWalletModal';
import { generateShareMessage } from '@/src/infrastructure/lib/share';
import { isAiNativeExperience } from './wizard-routing';
import { isPublishInProgress, withPublishRetry, PUBLISH_IN_PROGRESS_MESSAGE } from './publishRetry';
import {
  buildWizardReturnPath,
  setBuyCreditIntent,
  CREDIT_PURCHASE_RETURN_PARAM,
} from '@/src/modules/purchase/buyCreditIntent';
import { resolveCreditCollection } from '@/src/modules/purchase/resolveCreditCollection';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { useIsPartner } from '@/src/modules/auth/usePermissions';

type PublishPhase = 'idle' | 'creating' | 'publishing' | 'polling' | 'done' | 'error';

function formatDate(iso: string | undefined) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatTime(iso: string | undefined) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

type PublishEntitlementView = NonNullable<ReturnType<typeof usePublishEntitlement>['data']>;

/** What publishing costs THIS customer - shared by the inline panel and the final confirm dialog. */
function PublishCostSentence({ entitlement }: { entitlement: PublishEntitlementView }) {
  return (
    <>
        {entitlement.republishIncluded ? (
          <p className="text-on-surface-variant font-medium">
            <span className="font-bold text-on-surface">Republishing is included</span> — you already paid for this
            invitation, so no credit is used.
          </p>
        ) : entitlement.wouldUse === 'PAID_ENTITLEMENT' ? (
          // A Collection the customer bought for this design pays for it — never say "consume a
          // credit" or "Available: 0 credits" to someone who just paid.
          <p className="text-on-surface-variant font-medium">
            This publish will use <span className="font-bold text-on-surface">your paid purchase</span> for this
            design — no Publish Credit is used.
          </p>
        ) : (
          <p className="text-on-surface-variant font-medium">
            This publish will consume{' '}
            <span className="font-bold text-on-surface">
              {entitlement.templatePrice && entitlement.templatePrice > 1
                ? `₹${entitlement.templatePrice.toLocaleString('en-IN')} from your wallet`
                : '1 Publish Credit'}
            </span>
            .{' '}
            <span className="text-on-surface-variant">
              Available:{' '}
              {entitlement.walletBalance !== undefined && entitlement.walletBalance > 0
                ? `₹${entitlement.walletBalance.toLocaleString('en-IN')}`
                : `${entitlement.remainingCredits} ${entitlement.remainingCredits === 1 ? 'credit' : 'credits'}`}
              .
            </span>
          </p>
        )}
    </>
  );
}

function PublishConfirmDialog({
  open,
  canPublish,
  walletFrozen,
  awaitingCredit,
  creditWaitTimedOut,
  entitlement,
  remainingCredits,
  busy,
  buyingCredit,
  onCancel,
  onConfirm,
  onBuyCredit,
}: {
  open: boolean;
  canPublish: boolean | undefined;
  walletFrozen?: boolean;
  /** A credit purchase just completed and the grant is still in flight - never offer a second purchase. */
  awaitingCredit?: boolean;
  creditWaitTimedOut?: boolean;
  entitlement?: PublishEntitlementView | null;
  remainingCredits: number | null | undefined;
  busy: boolean;
  buyingCredit?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onBuyCredit?: () => void;
}) {
  const blocked = canPublish === false;
  const creditWord = remainingCredits === 1 ? 'credit' : 'credits';
  return (
    <TopLayerDialog
      open={open}
      onClose={onCancel}
      label="Publish this invitation?"
      variant="confirm"
      testId="publish-confirm"
    >
      <div
        className="flex min-h-[100dvh] items-center justify-center p-6"
        onClick={onCancel}
      >
        <div
          className="w-full max-w-md rounded-3xl bg-surface-lowest p-8 text-left shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <h2 className="text-2xl font-bold text-brand-deep">Publish this invitation?</h2>
          {blocked ? (
            walletFrozen ? (
              <p className="mt-3 text-sm text-status-failed font-medium">
                Your wallet is on hold while a payment dispute is reviewed. Please contact support@invitecloud.in — you can still save this invitation as a draft.
              </p>
            ) : awaitingCredit ? (
              <p role="status" className="mt-3 text-sm text-on-surface-variant font-medium">
                Payment received — confirming your Publish Credit. This usually takes under a minute.
              </p>
            ) : creditWaitTimedOut ? (
              <p role="status" className="mt-3 text-sm text-on-surface-variant font-medium">
                Your Publish Credit is taking longer than usual. Your payment is safe — save this invitation as a draft and check back in a few minutes.
              </p>
            ) : (
              <p className="mt-3 text-sm text-status-failed font-medium">
                No Publish Credits available — buy one to publish, or save this invitation as a draft for now.
              </p>
            )
          ) : entitlement ? (
            // Same wording as the inline panel: a paying customer is never told "consume a credit / Available: 0".
            <div className="mt-3 text-sm">
              <PublishCostSentence entitlement={entitlement} />
            </div>
          ) : (
            <p className="mt-3 text-sm text-on-surface-variant font-medium">
              This publish will consume <span className="font-bold text-on-surface">1 Publish Credit</span>.
              {typeof remainingCredits === 'number' && (
                <> Available: {remainingCredits} {creditWord}.</>
              )}
            </p>
          )}
          <p className="mt-2 text-sm text-on-surface-variant">
            Nothing is published until you confirm. You can go back and keep this as a draft.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              Back
            </Button>
            {blocked && !walletFrozen && !awaitingCredit && !creditWaitTimedOut && onBuyCredit && (
              <Button type="button" variant="primary" loading={buyingCredit} disabled={buyingCredit || busy} onClick={onBuyCredit}>
                Buy a Publish Credit
              </Button>
            )}
            <Button type="button" onClick={onConfirm} disabled={busy || blocked}>
              Publish invitation
            </Button>
          </div>
        </div>
      </div>
    </TopLayerDialog>
  );
}

export function StepPublish({ onBack, data, draftId, saveDraftNow }: StepProps) {
  const navigate = useNavigate();
  const isPartner = useIsPartner();
  const [phase, setPhase] = useState<PublishPhase>('idle');
  const [publishError, setPublishError] = useState('');
  // Derived from the message so any other error path overwriting publishError can never leave a stale retry button.
  const publishBusy = publishError === PUBLISH_IN_PROGRESS_MESSAGE;
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [insufficientMessage, setInsufficientMessage] = useState('');
  const [publishedUrl, setPublishedUrl] = useState('');
  // Publishing rights read model. We surface this so the wizard frames the
  // publish action in terms of Publish Credits (the primary entitlement) rather
  // than the legacy "1 active invitation" plan limit. Reads the entitlement
  // view, never raw credit columns, so a backend provider change needs no
  // change here.
  // reload() is called after a successful publish so the credit count displayed
  // in the UI decrements immediately without requiring a page refresh.
  //
  // `?creditPurchase=1` means we just came back from buying a credit (see
  // buyCreditIntent.ts): the grant can trail the "paid" screen by up to a
  // minute, so the hook waits for it instead of showing "No credits".
  const [searchParams] = useSearchParams();
  const returningFromCreditPurchase = searchParams.get(CREDIT_PURCHASE_RETURN_PARAM) === '1';
  const {
    data: entitlement,
    loading: entitlementLoading,
    reload: reloadEntitlement,
    awaitingCredit,
    creditWaitTimedOut,
  } = usePublishEntitlement({ awaitCredit: returningFromCreditPurchase, templateId: data.templateId, invitationId: draftId });

  // Synchronous guard: a double-click must not save twice or navigate twice.
  const buyingCreditRef = useRef(false);
  const [buyingCredit, setBuyingCredit] = useState(false);

  // Deep-link straight into checkout for the Collection bound to this draft's
  // template, remembering which draft to come back to. Falls back to the
  // catalog when no Collection matches (or the lookup fails) — the intent
  // survives that detour, so the return path still works.
  const handleBuyCredit = async () => {
    if (buyingCreditRef.current) return;
    buyingCreditRef.current = true;
    setBuyingCredit(true);
    setPublishError('');
    try {
      let invitationId: string | null;
      try {
        invitationId = saveDraftNow ? await saveDraftNow() : draftId ?? null;
      } catch (err) {
        setPublishError(err instanceof Error ? err.message : 'Failed to save draft.');
        return;
      }
      if (invitationId) {
        setBuyCreditIntent({ invitationId, returnTo: buildWizardReturnPath(invitationId) });
      }
      const collection = await resolveCreditCollection(data.templateId).catch(() => null);
      navigate(collection ? `/checkout/${collection.id}` : '/catalog');
    } finally {
      buyingCreditRef.current = false;
      setBuyingCredit(false);
    }
  };

  const handlePublish = async () => {
    setPublishError('');
    setPhase('creating');

    let invitationId: string | null = null;

    try {
      // The wizard autosaves a server-side DRAFT as the user works. Flush any
      // pending change so we publish the latest content, then publish that
      // draft. Fall back to a fresh create only if no draft exists yet (e.g.
      // autosave never ran).
      invitationId = saveDraftNow ? await saveDraftNow() : draftId ?? null;
      if (!invitationId) {
        const created = await createInvitation({
          templateId: data.templateId!,
          occasionType: data.occasionType!,
          eventName: data.eventName!,
          eventDate: data.eventDate!,
          eventVenue: data.eventVenue!,
          hostName: data.hostName!,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls ?? [],
          customData: data.customData,
        });
        invitationId = created.id;
      }
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Failed to create invitation.');
      return;
    }

    if (!invitationId) {
      setPhase('error');
      setPublishError('Could not prepare the invitation. Please try again.');
      return;
    }

    setPhase('publishing');

    try {
      await withPublishRetry(() => publishInvitation(invitationId!));
    } catch (err) {
      setPhase('error');
      if (isPublishInProgress(err)) {
        // Still locked after the auto-retry: nothing was charged or written, so just offer a retry.
        setPublishError(PUBLISH_IN_PROGRESS_MESSAGE);
        return;
      }
      const status = (err as { status?: number })?.status;
      const errData = (err as ApiError)?.data as { code?: string; error?: { code?: string } } | undefined;
      const code = errData?.code || errData?.error?.code;
      if (status === 402 || code === 'NO_PUBLISH_RIGHT') {
        void reloadEntitlement();
        setInsufficientMessage(err instanceof Error ? err.message : 'Not enough balance to publish.');
        setShowInsufficientModal(true);
        // Suppress redundant inline error banner when modal is displayed (Punch list #8)
        setPublishError('');
        return;
      }
      setPublishError(err instanceof Error ? err.message : 'Failed to submit for publishing.');
      return;
    }

    setPhase('polling');

    // Poll every 2 seconds until PUBLISHED or FAILED.
    //
    // AI-native experience templates (Barbie/Iron Man/Dark Knight and other
    // exp-* templates) run a real per-ceremony AI image generation pass on the
    // backend (up to 3 attempts per ceremony image, plus BullMQ's own 3 job
    // retries) before the invitation becomes PUBLISHED — that can legitimately
    // take well over a minute. The old flat 60s deadline gave up on these while
    // the backend job was often still running (and would have succeeded a bit
    // later), surfacing "Publishing timed out" for a publish that wasn't
    // actually broken. Non-AI templates (render → compress → deploy) are fast
    // and keep the original budget.
    const isAiNative = isAiNativeExperience(data);
    const deadline = Date.now() + (isAiNative ? 240_000 : 60_000);
    const poll = async (): Promise<void> => {
      if (Date.now() > deadline) {
        setPhase('error');
        setPublishError(
          isAiNative
            ? 'Publishing is taking longer than usual (AI-generated templates can take a few minutes). It may still complete — check your dashboard for status in a minute.'
            : 'Publishing timed out. Check your dashboard for status.'
        );
        return;
      }
      const status = await getInvitationStatus(invitationId);
      if (status.status === 'PUBLISHED') {
        setPublishedUrl(status.hostedUrl ?? '');
        setPhase('done');
        // Refresh the entitlement so the credit count shown to the user
        // reflects the newly consumed credit — no page reload required.
        void reloadEntitlement();
        return;
      }
      if (status.status === 'FAILED') {
        void reloadEntitlement();
        setPhase('error');
        // PQ-57: Fixed, friendly user message prevents technical or raw errors reaching the customer
        setPublishError(
          status.failureReason && status.failureReason.includes('timed out')
            ? status.failureReason
            : 'Publishing failed. Please try again.'
        );
        return;
      }
      await new Promise(r => setTimeout(r, 2000));
      return poll();
    };

    try {
      await poll();
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Unexpected error during publishing.');
    }
  };

  const handleSaveDraft = async () => {
    setPublishError('');
    setPhase('creating');
    try {
      // The draft is already autosaved server-side; just flush the latest edit
      // (or create it if autosave never ran) and head to the Drafts dashboard.
      if (saveDraftNow) {
        await saveDraftNow();
      } else {
        await createInvitation({
          templateId: data.templateId!,
          occasionType: data.occasionType!,
          eventName: data.eventName!,
          eventDate: data.eventDate!,
          eventVenue: data.eventVenue!,
          hostName: data.hostName!,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls ?? [],
          customData: data.customData,
        });
      }
      navigate('/dashboard/drafts');
    } catch (err) {
      setPhase('error');
      setPublishError(err instanceof Error ? err.message : 'Failed to save draft.');
    }
  };

  const [showPreview, setShowPreview] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publishedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPublishing = phase === 'creating' || phase === 'publishing' || phase === 'polling';
  const isDone = phase === 'done';
  const shareText = generateShareMessage(data, publishedUrl);

  return (
    <div className="w-full max-w-3xl flex flex-col relative">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-primary mb-2">Ready to Publish</h1>
        <p className="text-on-surface-variant font-medium">Review your invitation details before sending it out.</p>
      </div>

      <div className="grid gap-12">
        {/* Summary */}
        <section className="bg-surface-low/50 rounded-3xl p-8 border border-outline-variant/20 flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
          <div
            className="w-32 h-44 bg-primary/10 rounded-2xl overflow-hidden shadow-inner flex-shrink-0 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity group relative"
            onClick={() => setShowPreview(true)}
            title="Preview invitation"
          >
            <Icons.Calendar size={48} className="text-primary/30 group-hover:opacity-0 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-primary/20 rounded-2xl">
              <Icons.Eye size={28} className="text-primary" />
            </div>
          </div>

          <div className="flex flex-col flex-1 gap-3">
            <h2 className="text-3xl font-bold text-brand-deep">{data.eventName}</h2>
            <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
              <div className="flex items-center gap-2 text-on-surface-variant text-sm font-medium">
                <Icons.Calendar size={16} className="text-primary" />
                {formatDate(data.eventDate)} at {formatTime(data.eventDate)}
              </div>
              <div className="flex items-center gap-2 text-on-surface-variant text-sm font-medium">
                <Icons.MapPin size={16} className="text-primary" />
                {data.eventVenue}
              </div>
            </div>
            <div className="flex items-center justify-center md:justify-start gap-2 text-on-surface-variant text-sm font-medium mt-1">
              <Icons.Users size={16} className="text-primary" />
              <span>Hosted by {data.hostName}</span>
            </div>
            {data.customMessage && (
              <p className="text-sm text-on-surface-variant italic mt-1 line-clamp-2">"{data.customMessage}"</p>
            )}
            <div className="pt-2 flex items-center justify-center md:justify-start gap-3">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-outline-variant text-xs font-semibold text-on-surface hover:bg-surface-low transition-colors"
              >
                <Icons.Eye size={14} className="text-primary" />
                Preview Template
              </button>
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-primary/30 bg-primary/5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
              >
                <Icons.Edit3 size={14} />
                Change Template / Details
              </button>
            </div>
          </div>
        </section>

        {/* Publish entitlement notice — credits are the primary publish gate.
            We never show the legacy "Free plan allows only 1 active invitation"
            message here; instead we tell the user exactly what publishing costs. */}
        {!entitlementLoading && entitlement && (
          entitlement.walletFrozen ? (
            <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 flex items-center gap-3 text-sm" data-testid="wallet-hold-banner">
              <Icons.Warning size={18} className="text-status-failed flex-shrink-0" />
              <p className="text-status-failed font-medium">
                Your wallet is on hold while a payment dispute is reviewed. Please contact support@invitecloud.in.
              </p>
            </div>
          ) : entitlement.canPublish ? (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 px-6 py-4 flex items-center gap-3 text-sm">
              <Icons.Upload size={18} className="text-primary flex-shrink-0" />
              <PublishCostSentence entitlement={entitlement} />
            </div>
          ) : awaitingCredit ? (
            <div
              role="status"
              className="rounded-2xl bg-primary/5 border border-primary/20 px-6 py-4 flex items-center gap-3 text-sm"
            >
              <Icons.Loader size={18} className="animate-spin text-primary flex-shrink-0" />
              <p className="text-on-surface-variant font-medium">
                Payment received — confirming your Publish Credit. This usually takes under a minute.
              </p>
            </div>
          ) : creditWaitTimedOut ? (
            // Never offer a second purchase here: the payment went through and
            // the grant is still in flight — buying again would double-charge.
            <div
              role="status"
              className="rounded-2xl bg-surface border border-outline-variant/30 px-6 py-4 flex flex-col gap-1 text-sm"
            >
              <p className="font-semibold text-on-surface">Your Publish Credit is taking longer than usual.</p>
              <p className="text-on-surface-variant">
                Your payment is safe. Save this invitation as a draft and check back in a few minutes — the credit
                will appear automatically.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Icons.Upload size={18} className="text-status-failed flex-shrink-0" />
                <p className="text-status-failed font-medium">
                  No Publish Credits available — buy one to publish, or save this invitation as a draft for now.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isPartner && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => navigate('/dashboard/partner?tab=credits')}
                  >
                    Buy Partner Pack
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-shrink-0"
                  loading={buyingCredit}
                  disabled={buyingCredit}
                  onClick={() => void handleBuyCredit()}
                >
                  Buy a Publish Credit
                </Button>
              </div>
            </div>
          )
        )}

        {publishError && (
          <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm">
            {publishError}
            {publishBusy && (
              <Button variant="outline" size="sm" className="mt-3 block" onClick={handlePublish} disabled={isPublishing}>
                Try again
              </Button>
            )}
          </div>
        )}

        <div className="pt-8 border-t border-outline-variant/20 flex flex-col sm:flex-row justify-between gap-4 mt-8">
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={onBack} className="px-10 rounded-2xl" disabled={isPublishing}>
              Back
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowPreview(true)}
              className="px-6 rounded-2xl flex items-center gap-2"
              disabled={isPublishing}
            >
              <Icons.Eye size={18} /> Preview
            </Button>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="lg" onClick={handleSaveDraft} className="px-10 rounded-2xl" disabled={isPublishing}>
              Save as Draft
            </Button>
            <Button
              size="lg"
              type="button"
              onClick={() => setConfirmPublish(true)}
              className="px-10 rounded-2xl flex items-center gap-2 shadow-lg shadow-primary/20"
              disabled={isPublishing || (!entitlementLoading && entitlement != null && (!entitlement.canPublish || entitlement.walletFrozen))}
            >
              <Icons.Rocket size={20} />
              Publish Now
            </Button>
          </div>
        </div>
      </div>

      {/* Draft preview modal — available even without publish credits */}
      <DraftPreviewModal
        open={showPreview}
        data={{
          templateId: data.templateId ?? '',
          occasionType: data.occasionType ?? 'WEDDING',
          eventName: data.eventName,
          eventDate: data.eventDate,
          eventVenue: data.eventVenue,
          hostName: data.hostName,
          customMessage: data.customMessage,
          rsvpLink: data.rsvpLink,
          mediaUrls: data.mediaUrls,
          customData: data.customData as Record<string, unknown> | null | undefined,
        }}
        onClose={() => setShowPreview(false)}
        closeLabel="Back"
        continueTone="quiet"
        ctaLabel="Continue to publish"
        onCta={() => {
          setShowPreview(false);
          setConfirmPublish(true);
        }}
      />

      <PublishConfirmDialog
        open={confirmPublish}
        canPublish={entitlement?.canPublish && !entitlement?.walletFrozen}
        walletFrozen={entitlement?.walletFrozen}
        awaitingCredit={awaitingCredit}
        creditWaitTimedOut={creditWaitTimedOut}
        entitlement={entitlement}
        remainingCredits={entitlement?.remainingCredits}
        busy={isPublishing}
        buyingCredit={buyingCredit}
        onBuyCredit={() => {
          setConfirmPublish(false);
          void handleBuyCredit();
        }}
        onCancel={() => setConfirmPublish(false)}
        onConfirm={() => {
          setConfirmPublish(false);
          void handlePublish();
        }}
      />

      <InsufficientWalletModal
        open={showInsufficientModal}
        isWalletUser={isPartner}
        neededRupees={entitlement?.templatePrice && entitlement.templatePrice > 1 ? entitlement.templatePrice : undefined}
        availableRupees={entitlement?.walletBalance}
        neededCredits={1}
        availableCredits={entitlement?.remainingCredits ?? 0}
        message={insufficientMessage || publishError || undefined}
        onBuyCredits={() => void handleBuyCredit()}
        onClose={() => setShowInsufficientModal(false)}
      />

      {/* Publishing overlay */}
      <AnimatePresence>
        {(isPublishing || isDone) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-surface/95 backdrop-blur-xl z-[100] flex flex-col items-center justify-center p-8 text-center overflow-y-auto"
          >
            {isDone ? (
              <>
                <div className="w-20 h-20 mb-6 rounded-full bg-status-published/10 flex items-center justify-center">
                  <Icons.CheckCircle size={40} className="text-status-published" />
                </div>
                <h2 className="text-3xl font-bold text-brand-deep mb-2">Published!</h2>
                <p className="text-on-surface-variant font-medium mb-8">Your invitation is live and ready to share.</p>
                {publishedUrl && (
                  <>
                    <InvitationPublishPreviewCard data={data} publishedUrl={publishedUrl} />
                    <div className="flex flex-wrap justify-center gap-3 mt-6">
                      <Button
                        variant={copied ? 'secondary' : 'outline'}
                        onClick={handleCopyLink}
                        className="flex items-center gap-2 transition-all"
                      >
                        {copied
                          ? <><Icons.Check size={16} className="text-status-published" /> Copied!</>
                          : <><Icons.Copy size={16} /> Copy Link</>
                        }
                      </Button>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" className="flex items-center gap-2">
                          <Icons.WhatsApp size={16} /> WhatsApp
                        </Button>
                      </a>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(data.eventName ?? 'Invitation')}&body=${encodeURIComponent(publishedUrl)}`}
                      >
                        <Button variant="outline" className="flex items-center gap-2">
                          <Icons.Mail size={16} /> Email
                        </Button>
                      </a>
                    </div>
                  </>
                )}
                <Button onClick={() => navigate('/dashboard')} className="px-8 mt-6">
                  Back to Dashboard
                </Button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mb-8 relative">
                  <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent"
                  />
                </div>
                <h2 className="text-3xl font-bold text-brand-deep mb-4">
                  {phase === 'creating' ? 'Creating Invitation...' :
                   phase === 'publishing' ? 'Submitting for Publishing...' :
                   'Publishing Invitation...'}
                </h2>
                <p className="text-on-surface-variant font-medium max-w-sm">
                  {phase === 'polling'
                    ? 'Generating your unique link and preparing assets. This usually takes a few seconds.'
                    : 'Please wait...'}
                </p>
                <div className="mt-12 w-full max-w-xs bg-surface-low rounded-full h-2 overflow-hidden shadow-inner">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: phase === 'polling' ? '80%' : '40%' }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                    className="bg-primary h-full rounded-full"
                  />
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
